import { FC, Children, ReactElement, useEffect, useRef, isValidElement } from 'react';
import { camelCase } from 'lodash';
import Skeleton from 'react-loading-skeleton';
import { cx } from '@emotion/css';
import router from 'next/router';
import Link from 'next/link';
import { useIntersectionObserver } from 'usehooks-ts';
import { DSNotification, DSText, DSTitle } from '@stihl-design-system/components';

import { PageTitleType } from '@phoenix/design-system/frontend/ui/page-title/page-title';
import { getDesignSystemIcon } from '@phoenix/design-system/frontend/util/path.utils';
import { PhoenixIcon } from '@phoenix/design-system/frontend/ui/phoenix-icon/phoenix-icon';
import { HeadTitle } from '@phoenix/team-m/dashboard/frontend/shared/components/head-title';

import { useNotificationContext } from './use-notification-context';

interface ToolShedPageProps {
  title?: string;
  subHeadline?: string;
  isLoading?: boolean;
  pageTestId?: string;
  closeBtnTestId?: string;
  backLink?: {
    label: string;
    url: string;
    onClick?: () => void;
  };
  type?: PageTitleType;
  className?: string;
  children?: ReactElement | ReactElement[] | string;
}

const leftArrowIcon = getDesignSystemIcon('icon-arrow-left-16.svg');

type Prepend = FC<{ children }>;
const Prepend: Prepend = ({ children }) => children;

type Content = FC<{ children }>;
const Content: Content = ({ children }) => children;

type ToolShedPage = FC<ToolShedPageProps> & {
  Prepend: Prepend;
  Content: Content;
};

export const ToolShedPage: ToolShedPage = ({
  title,
  subHeadline,
  isLoading,
  backLink,
  children,
  pageTestId = camelCase(title),
  type,
  className = 'stihl-page-container',
}) => {
  const { pageNotifications } = useNotificationContext();
  const pageContainerRef = useRef<HTMLDivElement>(null);
  const { isIntersecting: isNotificationContainerIntersecting, ref: notificationContainerRef } = useIntersectionObserver({
    threshold: 0.8,
  });

  useEffect(() => {
    if (pageNotifications.filter(({ autoscroll }) => autoscroll).length && !isNotificationContainerIntersecting) {
      pageContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [pageContainerRef, pageNotifications]);

  const handleBackClick = (e: React.MouseEvent, url: string) => {
    e.preventDefault();
    if (backLink?.onClick) {
      backLink.onClick();
    }
    router.replace(url);
  };

  let prependContent: ReactElement | null = null;
  let mainContent: ReactElement | null = null;

  Children.forEach(children, (child) => {
    if (!isValidElement(child)) {
      return;
    }

    if (child.type === Prepend) {
      prependContent = child;
    } else if (child.type === Content) {
      mainContent = child;
    }
  });

  return (
    <div ref={pageContainerRef} className="overflow-x-hidden" data-testid={pageTestId || 'toolShedPage'}>
      {prependContent}
      {backLink && (
        <div className="mt-4 stihl-page-container">
          <Link
            href={backLink.url}
            className="flex gap-3 items-center focus-outline w-fit"
            onClick={(e) => handleBackClick(e, backLink.url)}
            data-testid="backLink"
          >
            <PhoenixIcon icon={leftArrowIcon} className="h-2 w-2" />
            <span className="truncate stihl-text-interactive-link-default hover:text-blue hover:underline">{backLink.label}</span>
          </Link>
        </div>
      )}
      {title && (
        <div className="mt-10 stihl-page-container">
          <HeadTitle title={title} />
          <DSTitle size={type === 'subpage' ? 'large' : 'x-large'}>{isLoading ? <Skeleton width={200} /> : title}</DSTitle>
        </div>
      )}
      {subHeadline && (
        <div className="stihl-page-container">
          <DSText className="max-w-xl">{subHeadline}</DSText>
        </div>
      )}
      {pageNotifications.length > 0 && (
        <div ref={notificationContainerRef} className="stihl-page-container space-y-6 py-6">
          {pageNotifications.map(({ id: notificationId, message, ...notification }) => (
            <DSNotification key={notificationId} {...notification} data-testid={notificationId}>
              {message}
            </DSNotification>
          ))}
        </div>
      )}
      <div className={cx('mb-14 desktop:mb-20', className)}>{mainContent ?? children}</div>
    </div>
  );
};

ToolShedPage.Prepend = Prepend;

ToolShedPage.Content = Content;

export default ToolShedPage;
